"use client";

import type { InfiniteCounterBlock } from "@mw/shared/payload-types";

import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";
import { animate, useMotionValue, useTransform } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { memo, Suspense, useEffect, useMemo, useRef, useState } from "react";

const SNAP_DURATION = 0.8;
const MIN_INTERVAL_MS = 500; // Don't update faster than this
const MAX_INTERVAL_MS = 2000; // Don't update slower than this

// 20 elements for smooth looping animation
const DIGIT_STRIP = [
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
];

// Shared styles
const DIGIT_GRADIENT = "linear-gradient(180deg, #EBE5E7 7.01%, rgba(235, 229, 231, 0.20) 100%)";

export interface InfiniteCounterProps extends Pick<
  InfiniteCounterBlock,
  "baseValue" | "startDate" | "incrementPerHour" | "maxValue" | "caption"
> {
  className?: string;
}

/**
 * Calculate update interval and increment range based on hourly increment rate.
 * Returns interval in ms and min/max increment per tick, scaled to match the target rate.
 */
function calculateAnimationParams(incrementPerHour?: number | null): {
  intervalMs: number;
  minIncrement: number;
  maxIncrement: number;
} {
  if (!incrementPerHour || incrementPerHour <= 0) {
    return { intervalMs: 1000, minIncrement: 1, maxIncrement: 5 };
  }

  // Target a reasonable number of updates per second (0.5-2 updates/sec)
  // Then calculate the increment needed to hit the hourly target
  const targetUpdatesPerSecond = 1;
  const intervalMs = Math.max(MIN_INTERVAL_MS, Math.min(MAX_INTERVAL_MS, 1000 / targetUpdatesPerSecond));

  // Calculate average increment needed per tick to hit hourly target
  const ticksPerHour = (3600 * 1000) / intervalMs;
  const avgIncrementPerTick = incrementPerHour / ticksPerHour;

  // Add variance: min is 50% of avg, max is 150% of avg (minimum 1)
  const minIncrement = Math.max(1, Math.floor(avgIncrementPerTick * 0.5));
  const maxIncrement = Math.max(minIncrement + 1, Math.ceil(avgIncrementPerTick * 1.5));

  return { intervalMs, minIncrement, maxIncrement };
}

function calculateInitialValue(
  baseValue: number,
  startDate?: string | null,
  incrementPerHour?: number | null,
  maxValue?: number | null,
): number {
  if (!startDate || !incrementPerHour || incrementPerHour <= 0) return baseValue;

  const start = new Date(startDate);
  const elapsedHours = Math.max(0, (Date.now() - start.getTime()) / (1000 * 60 * 60));

  const totalIncrement = Math.floor(elapsedHours * incrementPerHour);

  // If maxValue is set, wrap the value within the range [baseValue, maxValue]
  if (maxValue && maxValue > baseValue) {
    const range = maxValue - baseValue;
    return baseValue + (totalIncrement % range);
  }

  return baseValue + totalIncrement;
}

const numberContainerStyles = cva(
  "f-heading-02 tw-select-none tw-whitespace-nowrap tw-font-medium tw-leading-none tw-tracking-tight",
);
const digitStyles = cva("tw-block tw-bg-clip-text tw-text-center tw-leading-none tw-text-transparent");
const captionStyles = cva(
  "f-heading-03 text-default tw-relative tw-mx-auto tw-max-w-xl tw-text-center -tw-mt-6 md:-tw-mt-14 tw-text-pretty",
);

const AnimatedDigit = memo(function AnimatedDigit({ value }: { value: number }) {
  const motionValue = useMotionValue(value);
  const blurValue = useMotionValue(0);
  const prevValueRef = useRef(value);

  useEffect(() => {
    const prevValue = prevValueRef.current;
    prevValueRef.current = value;

    if (prevValue !== value) {
      const target = value < prevValue ? value + 10 : value;

      animate(motionValue, target, {
        duration: SNAP_DURATION,
        ease: [0.16, 1, 0.3, 1],
      }).then(() => motionValue.set(value));

      animate(blurValue, [0, 6, 0], {
        duration: SNAP_DURATION,
        ease: "easeOut",
        times: [0, 0.15, 1],
      });
    }
  }, [value, motionValue, blurValue]);

  const y = useTransform(motionValue, v => `${(-v * 100) / 20}%`);
  const filter = useTransform(blurValue, v => (v > 0.1 ? `blur(${v}px)` : "none"));

  return (
    <span
      className='tw-relative tw-inline-block tw-overflow-hidden'
      style={{ height: "1em", willChange: "transform, filter" }}
    >
      <m.span className='tw-flex tw-flex-col tw-items-center' style={{ y, filter }}>
        {DIGIT_STRIP.map((digit, i) => (
          <span key={i} className={digitStyles()} style={{ height: "1em", backgroundImage: DIGIT_GRADIENT }}>
            {digit}
          </span>
        ))}
      </m.span>
    </span>
  );
});

function InfiniteCounterInner({
  baseValue,
  startDate,
  incrementPerHour,
  maxValue,
  caption,
  className,
}: InfiniteCounterProps) {
  const t = useTranslations("ClientKeys.InfiniteCounter");
  const initialValue = useMemo(
    () => calculateInitialValue(baseValue, startDate, incrementPerHour, maxValue),
    [baseValue, startDate, incrementPerHour, maxValue],
  );
  const { intervalMs, minIncrement, maxIncrement } = useMemo(
    () => calculateAnimationParams(incrementPerHour),
    [incrementPerHour],
  );
  const [value, setValue] = useState(initialValue);

  // Pause animation when tab is not visible
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    const startInterval = () => {
      if (interval) return;
      interval = setInterval(() => {
        setValue(v => {
          const increment = Math.floor(Math.random() * (maxIncrement - minIncrement + 1)) + minIncrement;
          const newValue = v + increment;

          // If maxValue is set and valid (> baseValue), wrap back to baseValue when exceeded
          if (maxValue && maxValue > baseValue && newValue >= maxValue) {
            const range = maxValue - baseValue;
            return baseValue + ((newValue - baseValue) % range);
          }
          return newValue;
        });
      }, intervalMs);
    };

    const stopInterval = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        startInterval();
      }
    };

    if (!document.hidden) {
      startInterval();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopInterval();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [intervalMs, minIncrement, maxIncrement, maxValue, baseValue]);

  const formatted = value.toLocaleString("en-US");
  const chars = formatted.split("");
  const initialFormatted = initialValue.toLocaleString("en-US");

  // Calculate responsive font size based on character count
  // Using cqw (container query width) so it adapts to parent container, not viewport
  const charCount = chars.length;
  const maxCqw = Math.min(150 / charCount, 22); // Maximize size while fitting container

  return (
    <div
      className={cn("tw-relative tw-flex tw-flex-col tw-items-center", className)}
      style={{
        containerType: "inline-size", // Enable container queries
      }}
    >
      <div
        className='tw-relative tw-flex tw-w-full tw-items-center tw-justify-center'
        role='img'
        aria-label={t("live_counter_aria_label", { value: initialFormatted })}
      >
        <span className='tw-sr-only'>{initialFormatted}</span>

        <span
          className={numberContainerStyles()}
          aria-hidden='true'
          style={{ fontVariantNumeric: "tabular-nums", fontSize: `clamp(2rem, ${maxCqw}cqw, 14rem)` }}
        >
          {chars.map((char, i) => {
            const digit = parseInt(char, 10);
            if (!isNaN(digit)) {
              return <AnimatedDigit key={`digit_${i}`} value={digit} />;
            }
            return (
              <span
                key={`char_${i}`}
                className='tw-relative tw-inline-block tw-overflow-hidden'
                style={{ height: "1em" }}
              >
                <span className={digitStyles()} style={{ height: "1em", backgroundImage: DIGIT_GRADIENT }}>
                  {char}
                </span>
              </span>
            );
          })}
        </span>

        {/* Gradient overlay */}
        <div
          aria-hidden='true'
          className='tw-pointer-events-none tw-absolute tw-h-2 md:tw-h-6 tw-top-0 tw-w-full'
          style={{
            background: `linear-gradient(to bottom, var(--marker-background-color, white) 0%, transparent 100%)`,
          }}
        />
        <div
          aria-hidden='true'
          className='tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0'
          style={{
            height: "50%",
            background: `linear-gradient(to top, var(--marker-background-color, white) 0%, transparent 100%)`,
          }}
        />
      </div>

      {caption && <h3 className={captionStyles()}>{caption}</h3>}
    </div>
  );
}

export function InfiniteCounter(props: InfiniteCounterProps) {
  return (
    <Suspense fallback={<InfiniteCounterFallback caption={props.caption} />}>
      <InfiniteCounterInner {...props} />
    </Suspense>
  );
}

function InfiniteCounterFallback({ caption }: { caption?: string | null }) {
  return (
    <div className='tw-relative tw-flex tw-flex-col tw-items-center'>
      <div
        className={numberContainerStyles({ className: "tw-bg-clip-text tw-text-transparent" })}
        style={{
          fontVariantNumeric: "tabular-nums",
          fontSize: "clamp(2rem, 16vw, 14rem)", // 9 chars in "0,000,000" → 150/9 ≈ 16vw
          backgroundImage: DIGIT_GRADIENT,
          WebkitBackgroundClip: "text",
        }}
      >
        0,000,000
      </div>
      {caption && <h3 className={captionStyles()}>{caption}</h3>}
    </div>
  );
}
